import type { BrandColorRole, BrandFontId, BrandMemoryKind, BrandMemorySource } from '../brand-kit.js';
import type { CreativeJobStage, CreativeJobStageDetail } from '../creative-progress.js';
import type {
  BannerSizeId,
  ImageSetFrameRole,
  ImageSetType,
  ProductionAspect,
  ProductionDeliverableWarning,
  ProductionGate,
  ProductionGoal,
  ProductionKind,
  ProductionMode,
  ProductionShotStatus,
  ProductionStage,
  ProductionStatus,
  ProductionStrategy,
  ProductionTakeStatus,
  ProductionVariantKey,
  ProductionVariantKind,
  ShotKind,
} from '../production.js';
import type { Actionable, CardActionFields } from './actions.js';
import type { AgentCardKind } from './index.js';
import type { OpCardRoute } from './routes.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * C · KREATİF VE İÇERİK KARTLARI — alan ajanı: kreatif (Faz 8 dalga 2)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sahibi KREATİF ajanı (tarif `./index.ts`). Kart üreticileri
 * `apps/api/src/agent/cards/creative/`, görünümler web
 * `tool-renderers/creative.tsx`.
 *
 *   · Kreatif önizlemeleri İMZALI ve kısa ömürlü: kart yalnız `creativeId`
 *     taşır, ekran adresi çizimde tazeler (`GET /creatives?ids=` — `creative`
 *     kartındaki kural).
 *   · 🔴 GÜVENİLMEZ İÇERİK: rakip reklam metni düz metin olarak çizilir —
 *     markdown/HTML YOK (React metin düğümü; `dangerouslySetInnerHTML` yasak).
 *   · 🔴 KİŞİSEL VERİ: müşteri listesi CSV'si (`upload`, `customer_list`)
 *     şifreli, süreli kasada; kartta yalnız ek kimliği ve sayı — dosya adı
 *     bile yok. `comments` kartı 30 Eyl 2026'da D alanına taşındı
 *     (`./engagement.ts`).
 *   · Düğme = insan kararı (`./actions.ts`): seç / reklamda kullan / varyant
 *     üret `reply` (model devam eder), yeniden üret / iptal / rakibi takip et
 *     `app_op` (kart içi onay), reklamı durdur `platform_op`.
 */

/**
 * `creative` kartının (tipi `core.ts`te) KREATİF uzantısı — hepsi İSTEĞE
 * BAĞLI (eski satırlar geçerli kalsın).
 *
 *   `pick`     (varsayılan) kütüphane seçicisi — `list_creatives`.
 *   `gallery`  varyant galerisi — `creative_get`, üretim sonucu: kartın
 *              `creatives` listesi büyük küçük resimlerle, satır başına
 *              "bunu seç" / "yeniden üret" / "reklamda kullan" eylemleri
 *              (`scope` = `creativeId`).
 */
export interface CreativeCardExtras extends CardActionFields {
  /** Görünüm kipi: seçici (bugünkü) ya da galeri (varyant karşılaştırma). */
  mode?: 'pick' | 'gallery';
  /** Galerinin kaynağı — başlık bundan (`cards.creative.source.<source>`). */
  source?: 'library' | 'generated' | 'imported';
}

/**
 * Kreatif işinin türü — ekranda simge/ad. `model` = özel manken üretimi
 * (`studio_model_create`, Dalga 5b): render kuyruğundan değil `StudioModel`
 * satırından izlenir (`subject: 'studio_model'`).
 */
export type CreativeJobType =
  | 'image'
  | 'video'
  | 'banner'
  | 'resize'
  | 'upscale'
  | 'tryon'
  | 'imported'
  | 'model'
  | 'other';

/** Canlı kartın izlediği kayıt: render işi (`CreativeJob`) ya da özel manken (`StudioModel`). */
export type CreativeJobSubject = 'job' | 'studio_model';

export type CreativeAgentCard =
  /**
   * ÜRETİM İŞİ — canlı ilerleme, iptal, sonuç, toplu iş.
   *
   * Kart işin KİMLİKLERİNİ ve kayıt anındaki durumu taşır; ilerleme canlı
   * uçtan (`CreativeCardLiveData['creative_job']`). Bitmiş işin sonucu
   * `creativeId` ile imzalı adresten çizilir. Satır eylemleri (`scope` =
   * `jobId`): `cancel:<jobId>` (süren iş; ayrılan kredi iade), bitince
   * `open:<creativeId>` (kütüphanede aç), `regenerate:<creativeId>` (kart içi
   * onay — kredi), `size:<creativeId>` (başka boyut — model sürer),
   * `use:<creativeId>` (reklamda kullan).
   *
   * 🔴 CANLI ÜRETİM KARTI (Dalga 5b, 2 Eki 2026 — plan §4 "Üretim ilerlemesi
   * sohbette canlı kart"): kredi harcayan kreatif kartı UYGULANINCA sunucu bu
   * kartı modelden BAĞIMSIZ olarak sohbete ekler (`origin: 'apply'`, sentetik
   * araç adı `CREATIVE_LIVE_TOOL_NAME`). Yüzde/kalan süre GERÇEK aşamadan
   * (`creativeJobProgress`), ekran canlı olayla (`creatives` konusu) tazeler —
   * yoklama döngüsü YOK. Kullanıcı "kaçta" diye sormak zorunda kalmaz.
   */
  | Actionable<{
      kind: 'creative_job';
      jobs: {
        jobId: string;
        creativeId: string | null;
        type: CreativeJobType;
        status: 'queued' | 'running' | 'done' | 'failed' | 'canceled';
        progress: number | null;
        etaSeconds: number | null;
        errorCode: string | null;
        /*
         * ── Dalga 5b — canlı kart alanları; hepsi İSTEĞE BAĞLI (eski kart geçerli) ──
         */
        /** `job` (varsayılan) ya da `studio_model` — canlı kaynak buna göre okur. */
        subject?: CreativeJobSubject;
        /** Kreatif tipi (`AD_IMAGE`, `PRODUCT_VIDEO`…) — aşama süreleri (`creativeJobStageWeights`). */
        creativeType?: string | null;
        /** Kreatifin adı (kullanıcının/AI'ın brief'inden, kırpılmış — düz metin). */
        name?: string | null;
        /** İstenen oran — iskelet GERÇEK oranda çizilir (bitmeden önce boyut yok). */
        aspect?: { width: number; height: number } | null;
        /** Bu işe ayrılan kredi — düşerse "N kredi iade edildi". */
        reservedCredits?: number | null;
      }[];
      credits: { reserved: number } | null;
      /** Toplu stüdyo işi mi (banner/deneme/büyütme partisi). */
      bulk?: boolean;
      /** Listenin tamamı — kart ≤24 işi taşır. */
      total?: number;
      /**
       * Kartın kaynağı: `apply` = onaylanan kartın ardından SUNUCUNUN eklediği
       * canlı kart; `tool` (yok) = modelin okuması (`creative_jobs`/`creative_job_get`).
       */
      origin?: 'apply' | 'tool';
      /** `apply` kartında işi başlatan araç (`creative_banner`…) — başlık bundan. */
      tool?: string | null;
    }>
  /**
   * KREATİF PERFORMANSI — küçük resim + CTR/sıklık eğrisi + yorulma rozeti.
   *
   * Satır bir REKLAM (`adRef` = Mentorivo `Ad.id`); `creativeId` stüdyoda
   * üretilmişse. Eğri (günlük CTR/sıklık) canlı uçtan — karta yazılan
   * `series` boş kalabilir (eski satırlar dolu olabilir). Eylemler:
   * `pause:<adRef>` (platform işlemi, kart içi onay) ve `variant:<adRef>`
   * (modelden varyant ister).
   */
  | Actionable<{
      kind: 'creative_performance';
      platform: string | null;
      range: { from: string; to: string };
      currency: string | null;
      items: {
        id: string;
        creativeId: string | null;
        adRef: string | null;
        name: string | null;
        /** Satırın platformu — karma listede her satırın işareti. */
        platform?: string | null;
        spend: number | null;
        impressions: number | null;
        ctr: number | null;
        frequency: number | null;
        series: { date: string; ctr: number | null; frequency: number | null }[];
        fatigue: 'none' | 'early' | 'fatigued' | null;
        /** 0–100 yorgunluk puanı (`CreativeDiagnosis.fatigueScore`). */
        fatigueScore?: number | null;
      }[];
      /** Listenin tamamı — kart ≤12 satır taşır. */
      total?: number;
    }>
  /**
   * RAKİP REKLAMLARI — reklam kitaplığı galerisi. Metin GÜVENİLMEZ (düz metin).
   * Sayfa adı işletme sayfası (kişi değil).
   *
   * `competitor_search` (ada göre sayfa arama) aynı kartı `pages` ile çizer:
   * her sayfa için "takip et" (`track:<pageId>` — `competitor_profile_create`,
   * kart içi onay). Aramada reklam örnekleri de galeride.
   */
  | Actionable<{
      kind: 'competitor_ads';
      source: 'meta_ad_library' | 'google_transparency' | 'tiktok_library' | 'linkedin_library' | 'internal';
      query: string | null;
      ads: {
        id: string;
        pageName: string | null;
        platforms: string[];
        startedAt: string | null;
        active: boolean | null;
        mediaType: 'image' | 'video' | 'carousel' | 'text' | null;
        /** Reklam metni — ÜÇÜNCÜ KİŞİ metni, düz metin çizilir. */
        body: string | null;
        snapshotUrl: string | null;
        /** Kaç gündür yayında (kitaplığın başlangıç tarihinden). */
        daysRunning?: number | null;
      }[];
      total: number;
      /** Arama sonucu sayfalar (yalnız `competitor_search`). */
      pages?: {
        pageId: string;
        pageName: string | null;
        /** Aramada bu sayfadan görülen reklam sayısı (örneklem). */
        adCount: number;
        /** Zaten takip ediliyor mu (profil var). */
        tracked: boolean;
      }[];
      /** Kitaplık yalnız AB'ye yayınlanan ticari reklamları döndürüyor (DSA). */
      euOnly?: boolean;
    }>
  /**
   * KATALOG SAĞLIĞI — ürün sorunları + düzelt.
   *
   * `products` en kötü ürünler (başlık mağaza metni, kişisel değil) ve satır
   * eylemi `fix:<productId>` (modelden düzeltme ister → `fix_product_content`).
   * Mağaza kataloğa YAZAMIYORSA (ikas, Trendyol…) düzeltme kartı `manual` taşır:
   * önerilen metin + "mağaza panelinde ne yapılmalı" adımları + kopyala.
   */
  | Actionable<{
      kind: 'catalog_health';
      store: { platform: string; name: string | null } | null;
      score: number | null;
      totalProducts: number;
      issues: {
        code: string;
        severity: 'block' | 'warn' | 'info';
        count: number;
        /** Örnek ürün kimlikleri (≤20). */
        productIds: string[];
      }[];
      checkedAt: string | null;
      products?: {
        productId: string;
        title: string;
        score: number | null;
        issues: string[];
        /** Metinle düzeltilebilen maddeler (`AI_FIXABLE_CODES`). */
        fixable: string[];
        platform: string | null;
      }[];
      /** Mağaza bağlayıcısı kataloğa yazabiliyor mu (`canWriteCatalog`). */
      canWrite?: boolean | null;
      /** Yazamayan mağazada önerilen metin — kullanıcı mağaza panelinde yapıştırır. */
      manual?: {
        productId: string;
        productTitle: string;
        platform: string;
        /**
         * Önerilen metinler. ⚠️ Adı `fields` DEĞİL: kişisel veri kemeri
         * (`pii-seal.ts` → `isAnswerField`) nesne dizisi taşıyan `fields`i lead
         * formu cevabı sayıp kartı düşürüyor.
         */
        changes: { key: 'title' | 'description' | 'productType' | 'tags'; before: string | null; after: string }[];
      } | null;
    }>
  /**
   * DOSYA İSTEĞİ — AI kullanıcıdan görsel/video/logo/belge/müşteri listesi ister.
   *
   * Kartın içinde yükleme alanı var; seçilen dosya yazma kutusunun ek hattından
   * (küçültme, HEIC/SVG dönüşümü, CSV kasası) geçip sohbete EK olarak gider —
   * model onu `[Attachment aN …]` satırıyla görür ve sunucu kartı "yüklendi"
   * hâline getirir (`files[].ref` = ek kimliği).
   * 🔴 `customer_list`: yalnız ek kimliği + satır sayısı; dosya adı yok.
   */
  | Actionable<{
      kind: 'upload';
      purpose: 'image' | 'video' | 'logo' | 'document' | 'customer_list';
      accept: string[];
      maxBytes: number;
      maxFiles: number;
      status: 'waiting' | 'uploaded' | 'expired';
      files: { ref: string; mime: string; bytes: number; rows: number | null }[];
      /** Neden istendiği — modelin kısa cümlesi (≤160, düz metin). */
      note?: string | null;
    }>
  /**
   * LOGO ADAYLARI — bağlı reklam hesaplarındaki marka profil görselleri
   * (`brand_logo_from_platform`, 1 Eki 2026).
   *
   * Adaylar araç çağrısında GÜVENLİ indiriciyle geçici yükleme alanına iniyor
   * (`ws/<id>/uploads/…`); kart yalnız o DEPO ANAHTARINI taşır — imzalı adres
   * değil, platformun CDN adresi hiç değil (süreli ve imzalı; kartta ölürdü).
   * Ekran önizlemeyi çizimde imzalatır (`GET /agent/attachments/preview`).
   * Satır eylemi `save:<id>` → `brand_logo_add` (`app_op`, insan tıklaması,
   * kredi yok); `none` → modele "hiçbiri değil" (`reply`).
   *
   * `label` İŞLETME adı (Sayfa / şirket sayfası / işletme hesabı) — kişi
   * kimliği taşıyan kaynak (içerik üretici yetkisi) aday OLMUYOR.
   */
  | Actionable<{
      kind: 'logo_candidates';
      candidates: {
        id: string;
        source: LogoCandidateSource;
        platform: string;
        label: string | null;
        /** Geçici depo anahtarı; indirilemediyse `null` (`error` dolu). */
        assetKey: string | null;
        mime: string | null;
        width: number | null;
        height: number | null;
        bytes: number | null;
        /** SVG/GIF ise PNG'ye çevrildi. */
        converted: 'svg' | 'gif' | null;
        error: string | null;
      }[];
      /** Platform başına sonuç — "LinkedIn bağlı değil", "izin yok". */
      platforms: { platform: string; status: 'found' | 'none' | 'not_connected' | 'unavailable'; code: string | null }[];
    }>
  /**
   * REKLAM VİDEOSU (prodüksiyon) — `production_get` / `production_deliverables`
   * (Faz 8a sohbet eşitliği, 1 Eki 2026). Arayüzde "Reklam videosu"; "film"
   * yalnız motorun iç adı (plan §4 — bu bir REKLAM KREATİFİ).
   *
   * 🔴 Kullanıcının gördüğü akış SADE: Ürün → Fikir → Taslak → Hazır (plan §4,
   * "Reklam platformuyuz, film yapmıyoruz"). Görünüm adları (`storyboard`,
   * `shot_failed`) motorun iç sözlüğü — ekran metni "taslak", "bölüm", "sürüm".
   *
   * Tek tür, KAPIYA göre alt görünüm (`view` — sunucu seçer, ekran çizer):
   *   questions     ASK modunun 2–4 sorusu; şık başına önizleme karesi + cevap düğmesi
   *   concepts      fikir seçimi (biçim, ilk saniyeler, neden satar, öneri rozeti)
   *   script        DIRECT'in senaryo kapısı — beat'ler + "bu senaryoyla devam"
   *   storyboard    taslak kareler (varyanta göre gruplu) + çıktı BİÇİMİ (gerekçe,
   *                 seçeneklerin fiyatı, değiştir düğmeleri) + "Onayla — en fazla X"
   *                 (kredi rızası)
   *   shot_failed   kurtarılamayan çekim: yeniden dene (yeni rıza) / çıkar
   *   revision      onay sonrası maliyetli revizyonun ek rızası: kabul / ret
   *   progress      motor çalışıyor — aşama rayı, çekimler, iptal
   *   deliverables  teslim — varyant başına (Açılış A/B/C ya da fikir) master +
   *                 oranlar/kesimler, "reklamda kullan"; eksik parça uyarısı
   *   ended         başarısız / iptal — kod + (varsa) önceki teslimler
   *
   * Düğme = İNSAN kararı (`./actions.ts`): cevap/konsept/onay/take/yeniden
   * çekim/iptal/revizyon `app_op` (aracın yürütücüsü — REST'le aynı servis
   * yöntemi, kart içi onay adımı kredi harcayanlarda); "sıradaki adımı göster"
   * ve "reklamda kullan" `reply` (model devam eder).
   *
   * 🔴 İMZALI ADRES YOK: anahtar kare, şık önizlemesi, take posteri ve teslim
   * dosyası kısa ömürlü — kart yalnız KİMLİK taşır, adresler + anlık durum
   * çizimde canlı uçtan (`CreativeCardLiveData['production']`, yoklama).
   * 🔴 PROMPT YOK, USD YOK: yalnız kullanıcının da gördüğü spec özeti ve kredi.
   * 🔴 Eylem parametresinde `answers` ANAHTARI YOK — kişisel veri kemeri onu
   * lead formu cevabı sayıp kartı düşürür; cevap düğmesi `questionId`+`optionId`.
   * Liste tavanları sunucuda (çekim ≤24, take ≤4, teslim ≤24 — en yeni kurgu önce); `shotTotal`
   * tamamını söyler.
   */
  | Actionable<{
      kind: 'production';
      productionId: string;
      /** Seçilen konseptin başlığı ya da ürün adı (kullanıcının/LLM'in metni — düz metin). */
      title: string | null;
      /**
       * PROVA prodüksiyonu (Faz 10a) — yalnız `true` iken var. Teslimler yedek
       * medya: kart "Prova" der, yayın yolu yok (sunucu reddeder). Eski kartta yok.
       */
      rehearsal?: true;
      /**
       * Prodüksiyonun TÜRÜ (Dalga 5) — reklam videosu ya da GÖRSEL KAMPANYA
       * (seri / karusel / banner seti). Aynı motor, aynı sade akış (Ürün →
       * Fikir → Taslak → Hazır); görsel kampanyada taslak = kare ızgarası,
       * hazır = set önizlemesi (seri ızgara, karusel kaydırma, banner boyut
       * panosu). Eski kartta yok = `VIDEO`.
       */
      productionKind?: ProductionKind;
      /**
       * Görsel kampanyanın isteği — set türü, kare sayısı (seri/karusel) ya da
       * boyutlar (banner seti). Videoda yok/`null`.
       */
      imageSet?: { setType: ImageSetType; count: number | null; sizes: BannerSizeId[] } | null;
      /**
       * Görsel kampanyanın KARE PLANI (`SET_PLAN` sonrası) — karenin görevi,
       * üstüne basılacak başlık/gövde (gerçek fontla, FINISH'te) ve sade dilde
       * görsel tarif. `shotId` karenin üretim satırı (anahtar kare canlı uçtan
       * `shots[].keyframeUrl`). Kullanıcının/LLM'in metni — düz metin.
       */
      frames?: {
        index: number;
        role: ImageSetFrameRole;
        headline: string | null;
        body: string | null;
        visualBrief: string;
        shotId: string | null;
      }[] | null;
      /** Karuselin anlatısı (kartlar bir arada ne anlatıyor) ve çağrı metni. */
      setNarrative?: string | null;
      setCta?: string | null;
      /** Yorulan bir reklamın YENİLEMESİ mi (kaynak reklam `Ad.id`) — kartta köken notu. */
      sourceAdId?: string | null;
      view: ProductionCardView;
      status: ProductionStatus;
      stage: ProductionStage;
      gate: ProductionGate | null;
      mode: ProductionMode;
      aspect: ProductionAspect;
      /** `null` = süreyi AI seçiyor (yerleşimin varsayılanı). */
      durationSeconds: number | null;
      /** Reklamın hedefi (başlatma isteğinden); yoksa AI çıkarıyor. Eski kartta yok. */
      goal?: ProductionGoal | null;
      progress: { stage: ProductionStage; done: number; total: number };
      credits: { reserved: number; charged: number };
      /** "En fazla X, tahmini Y" — onaydan önce güncel çekimlerden, sonra rıza verilen. */
      estimate: { storyboardCredits: number; expectedCredits: number | null; maxCredits: number | null } | null;
      /** Prodüksiyonun hata kodu (`ProductionErrorCode`) — ekran `cards.production.error.<kod>`. */
      errorCode: string | null;
      version: number;
      /** Yalnız `questions` görünümünde. */
      questions?: {
        id: string;
        text: string;
        options: { id: string; label: string }[];
        answerOptionId: string | null;
        answerText: string | null;
      }[];
      /** Yalnız `concepts` görünümünde (seçimden sonra: `concept`). */
      concepts?: {
        id: string;
        title: string;
        /** Anlatım türü (`PRODUCTION_CONCEPT_GENRES`) — biçim yoksa (eski fikir) etiket o. */
        genre: string;
        /**
         * Reklam biçimi (`PRODUCTION_AD_FORMATS`: problem→çözüm, ürün gösterimi,
         * UGC tarzı…) — kartın etiketi. Dalga 4'ten önceki fikirde yok.
         */
        format?: string;
        insight: string;
        logline: string;
        tone: string;
        clicheScore: number;
        recommended: boolean;
        recommendationReason: string | null;
        /**
         * PERFORMANS MANTIĞI (Dalga 4) — ilk 1–1,5 sn'nin kaydırmayı durduran
         * açılışı ve "neden satar". Eski kartta yok.
         */
        hookIdea?: string;
        whyItWorks?: string;
      }[];
      /** Seçilmiş konseptin özeti — başlık altında. */
      concept?: { id: string; title: string; genre: string; format?: string; insight: string } | null;
      /** Yalnız `script` görünümünde. */
      script?: {
        durationSec: number;
        beats: {
          id: string;
          role: string;
          startSec: number;
          endSec: number;
          description: string;
          vo: string | null;
          super: string | null;
        }[];
        cta: string | null;
      } | null;
      shots: {
        id: string;
        order: number;
        status: ProductionShotStatus;
        durationSec: number;
        shotKind: ShotKind;
        framing: string;
        purpose: string;
        hasKeyframe: boolean;
        chosenTakeId: string | null;
        /** Güncel spec'in take'leri (≤4) — poster canlı uçtan. */
        takes: { id: string; index: number; status: ProductionTakeStatus }[];
        /** Talimatsız yeniden çekimin rızası — yalnız onaydan sonra, HAZIR/BAŞARISIZ çekimde. */
        retry: { expectedCredits: number; maxCredits: number } | null;
        /** Hangi varyantların reklamında (boş = ortak). Eski kartta yok. */
        variantKeys?: ProductionVariantKey[];
      }[];
      /** Düşmemiş çekimlerin tamamı — kart ≤24 taşır. */
      shotTotal: number;
      /**
       * ÇIKTI BİÇİMİ (Dalga 4) — AI'ın kararı (`decidedBy: 'ai'`) ya da
       * kullanıcının seçimi: tek reklam / aynı fikrin açılış (hook) varyantlı
       * A/B seti / farklı fikirlerden birkaç reklam. Gerekçe ve etiketler
       * kullanıcının dilinde LLM metni (düz metin). Varyant başına reklamındaki
       * çekimlerin kimlikleri (taslak görünümü gruplar); açılış setinde
       * açılışın kendisi. Strateji adımından önce `null`/yok.
       */
      plan?: {
        strategy: ProductionStrategy;
        decidedBy: 'ai' | 'user';
        /** `null` = gerekçe yok (biçim kaydı olmayan tek reklam). */
        rationale: string | null;
        /** Onaydan önce kalan biçim değiştirme hakkı. */
        changesLeft: number;
        variants: {
          key: ProductionVariantKey;
          kind: ProductionVariantKind;
          label: string;
          rationale: string | null;
          hook: { description: string; super: string | null } | null;
          shotIds: string[];
        }[];
      } | null;
      /**
       * Biçim seçeneklerinin fiyatı ("en fazla X, tahmini Y") — yalnız onaydan
       * önce ve biçim değişebilirken (onay ekranı, DIRECT senaryo kapısı).
       * Değiştir düğmeleri `strategy:<biçim>:<n>` (`production_set_strategy`).
       */
      strategyQuotes?: {
        strategy: ProductionStrategy;
        variantCount: number;
        expectedCredits: number;
        maxCredits: number;
        current: boolean;
      }[] | null;
      /**
       * TASLAKTAN ÖNCE biçim seçeneklerinin EK taslak kredisi (Dalga 5, senaryo
       * kapısı — `ProductionDetail.draftQuotes`). `extraCredits` 0 = başta
       * kilitlenen tutar kapsıyor; > 0 = düğme o tutara rıza ister (kart içi
       * onay, `confirmCredits`). `auto` = AI bu biçimi kendi başına seçebilir.
       */
      draftQuotes?: {
        strategy: ProductionStrategy;
        variantCount: number;
        extraCredits: number;
        auto: boolean;
        current: boolean;
      }[] | null;
      /** Rıza bekleyen (ya da son) revizyon — `revision` görünümünde dolu. */
      revision?: {
        id: string;
        instruction: string;
        status: string;
        summary: string | null;
        costDeltaCredits: number | null;
        postDelivery: boolean;
      } | null;
      deliverables: {
        creativeId: string;
        rendition: string;
        cut: number;
        /** Hangi varyantın dosyası (ekyok = `v1`). Eski kartta yok. */
        variantKey?: ProductionVariantKey;
        aspect: ProductionAspect;
        width: number | null;
        height: number | null;
        durationMs: number | null;
        /** Eksik parçayla teslim (ör. müziksiz) — boş = tam. Eski kartta yok. */
        warnings?: ProductionDeliverableWarning[];
        /**
         * Görsel kampanya teslimi: serinin/karuselin kaçıncı karesi (1'den) ya
         * da banner setinin boyutu (`300x250`). Videoda yok/`null`.
         */
        frameIndex?: number | null;
        bannerSize?: BannerSizeId | null;
      }[];
      /** Güncel kurgu (0 = ilk teslim; teslim sonrası her uygulanmış revizyon +1). */
      cut: number;
    }>
  /**
   * MARKA KİTİ + AI NOTLARI — `brand_kit_get` / `brand_memory_list` (Dalga 5,
   * 2 Eki 2026). Kreatif stüdyosunun "Marka" sayfasının sohbet karşılığı:
   * üstte kit (ad, slogan, ton, yap/yapma, renkler, fontlar, logo, son kart,
   * müzik zevki), altta AI'ın notlarının sayısı ve (odakta) listesi.
   *
   *   focus `kit`     kit özeti + not sayısı (tür başına) + sabitlenmiş/son ≤5 not
   *   focus `memory`  not listesi (≤12) — satır başına sabitle/çöz ve sil
   *
   * Satır eylemleri `pin:<id>` (`brand_memory_pin`, sabit girdi `pinned`) ve
   * `delete:<id>` (`brand_memory_delete`, kart içi onay) — insanın tıklaması,
   * kredi yok. 🔴 Not metni kullanıcının/AI'ın yazdığı serbest metin — düz
   * metin çizilir; kart kişisel veri taşımaz (kişi adı yok, yalnız kaynak türü).
   * Logo yalnız kimlik + ad (imzalı adres yok).
   */
  | Actionable<{
      kind: 'brand_kit';
      focus: 'kit' | 'memory';
      /** Kit hiç kaydedilmedi — kart "Marka sayfasında ya da burada doldur" der. */
      empty: boolean;
      name: string | null;
      tagline: string | null;
      tone: string | null;
      voiceDo: string[];
      voiceDont: string[];
      colors: { hex: string; role: BrandColorRole }[];
      fonts: { display: BrandFontId | null; body: BrandFontId | null };
      logo: { id: string; name: string } | null;
      endCard: { cta: string | null; showPrice: boolean };
      musicTaste: string | null;
      updatedAt: string | null;
      /** AI notlarının sayısı (süresi dolmuşlar hariç) — toplam, sabitlenmiş, tür başına. */
      notes: { total: number; pinned: number; byKind: Partial<Record<BrandMemoryKind, number>> };
      /** Gösterilen notlar — kitte ≤5 (sabitlenmiş önce), listede ≤12. */
      items: {
        id: string;
        kind: BrandMemoryKind;
        text: string;
        source: BrandMemorySource;
        pinned: boolean;
        createdAt: string;
        expiresAt: string | null;
      }[];
    }>
  /**
   * VARYANT A/B PERFORMANSI — `production_performance` (Dalga 5). Bir
   * prodüksiyonun varyantları (Açılış A/B/C ya da Fikir A/B) yayında nasıl
   * gidiyor: harcama, gösterim, kanca oranı (3 sn izlenme / gösterim; TikTok
   * 2 sn), tutma (ThruPlay / oynatma), TO, (varsa) EBM/ROAS. Kazanan YALNIZ
   * veri eşiğinin üstünde (`minImpressions` varyant başına) ilan edilir —
   * altında "veri toplanıyor". Görsel kampanyada video oranları `null`.
   *
   * Eylemler: `publish_ab` (`reply` — model hedef reklam setini sorup
   * `production_publish_ab`i sahneler), `refresh` (`reply` — kazanandan yeni
   * açılışlar, `production_refresh`), sayfa bağlantısı. Kimlik + sayı; kişisel
   * veri yok.
   */
  | Actionable<{
      kind: 'variant_performance';
      productionId: string;
      title: string | null;
      productionKind?: ProductionKind;
      /** Metriklerin penceresi (çalışma alanı gününde); yayın yoksa `null`. */
      range: { from: string; to: string } | null;
      currency: string | null;
      /** Kazanan ilanı için varyant başına en az gösterim. */
      minImpressions: number;
      /**
       *   not_published  varyantlar henüz bir reklamda değil
       *   collecting     yayında ama veri eşiğin altında
       *   ready          her varyant eşikte — karşılaştırma anlamlı
       *   single         tek reklam (tek varyant ya da görsel set) yayında —
       *                  karşılaştırılacak bir şey yok, kazanan sözü yok
       */
      state: 'not_published' | 'collecting' | 'ready' | 'single';
      /** Kazanan (eşiğin üstünde ve fark anlamlıysa) — `metric` neyle kazandığı. */
      winner: {
        key: ProductionVariantKey;
        metric: VariantWinMetric;
        /** Kazananın ikinciye göre oransal üstünlüğü (0,25 = %25); bilinmiyorsa `null`. */
        lift: number | null;
      } | null;
      variants: {
        key: ProductionVariantKey;
        kind: ProductionVariantKind;
        /** LLM'in etiketi (düz metin); ekran harfli adı kendisi kurar (Açılış B). */
        label: string | null;
        /** Varyantın ana teslimi (kütüphane kreatifi). */
        creativeId: string | null;
        /** Bu varyantın yayındaki reklam sayısı. */
        ads: number;
        spend: number | null;
        impressions: number | null;
        /** 0–1 oranları. */
        hookRate: number | null;
        holdRate: number | null;
        ctr: number | null;
        conversions: number | null;
        cpa: number | null;
        roas: number | null;
        enoughData: boolean;
        winner: boolean;
      }[];
    }>;

/** Kazananın ölçütü — kartın "neden kazandı" cümlesi. */
export const VARIANT_WIN_METRICS = ['roas', 'cpa', 'ctr', 'hook_rate', 'hold_rate'] as const;
export type VariantWinMetric = (typeof VARIANT_WIN_METRICS)[number];

/** `production` kartının alt görünümleri — sunucu kapıya göre seçer. */
export const PRODUCTION_CARD_VIEWS = [
  'questions',
  'concepts',
  'script',
  'storyboard',
  'shot_failed',
  'revision',
  'progress',
  'deliverables',
  'ended',
] as const;
export type ProductionCardView = (typeof PRODUCTION_CARD_VIEWS)[number];

/** Logo adayının kaynağı — ekran işaret + ad için. */
export const LOGO_CANDIDATE_SOURCES = ['facebook_page', 'instagram', 'linkedin_org', 'tiktok_identity', 'x_profile'] as const;
export type LogoCandidateSource = (typeof LOGO_CANDIDATE_SOURCES)[number];

export const CREATIVE_CARD_KINDS = [
  'creative_job',
  'creative_performance',
  'competitor_ads',
  'catalog_health',
  'upload',
  'logo_candidates',
  'production',
  'brand_kit',
  'variant_performance',
] as const;

/**
 * Araç adı → kart. Ekran araçları (`app-tools/creative.tools.ts`,
 * `competitor.tools.ts`) `AppToolSpec.card` ile, çekirdek araçlar
 * (`get_catalog_health`, `fix_product_content`) `ToolOutcome.ui` ile üretir.
 */
export const CREATIVE_TOOL_CARDS: Readonly<Record<string, readonly AgentCardKind[]>> = {
  creative_get: ['creative'],
  creative_jobs: ['creative_job'],
  creative_job_get: ['creative_job'],
  creative_analysis: ['creative_performance'],
  competitor_ads: ['competitor_ads'],
  competitor_search: ['competitor_ads'],
  get_catalog_health: ['catalog_health'],
  fix_product_content: ['catalog_health'],
  request_upload: ['upload'],
  brand_logo_from_platform: ['logo_candidates'],
  /* Faz 8a — reklam videosu: tek kart, kapıya göre alt görünüm. */
  production_get: ['production'],
  production_deliverables: ['production'],
  /* Dalga 5 — marka kiti + AI notları (aynı kart, iki odak) ve varyant A/B özeti. */
  brand_kit_get: ['brand_kit'],
  brand_memory_list: ['brand_kit'],
  production_performance: ['variant_performance'],
};

export const CREATIVE_OP_CARDS: readonly OpCardRoute<AgentCardKind>[] = [];

// ═══════════════════════════════════════════════════════════════════════════
// Canlı üretim kartı — onayda sunucunun eklediği kart (Dalga 5b, 2 Eki 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sunucunun eklediği canlı kartın SENTETİK araç adı (`toolCalls[].name`).
 * Bir araç sonucu değil (model çağırmadı): onaylanan kartın uygulanmasından
 * sonra sohbete yazılan ayrı bir asistan satırı. Satırın modele giden ham
 * kopyası BOŞ — kart modelin geçmişine girmez (`replayHistory` süzüyor).
 */
export const CREATIVE_LIVE_TOOL_NAME = 'creative_live' as const;

/**
 * Sentetik kart kimliği — `GET /agent/cards/:callId/data` deseniyle uyumlu
 * (`[A-Za-z0-9_-]{1,120}`). Aynı uygulamanın kartı iki kez eklenmesin diye
 * DETERMİNİSTİK: tohum öneri kimliği ya da kart eylemi anahtarı.
 */
export function creativeLiveCallId(ref: string): string {
  return `live_${ref.replace(/[^A-Za-z0-9_-]/g, '')}`.slice(0, 120);
}

/**
 * HANGİ KART UYGULANINCA HANGİ CANLI KART DOĞAR — kredi harcayan her kreatif
 * işi. Reklam videosu / görsel kampanya (prodüksiyon) için canlı kart mevcut
 * `production` kartının kendisi (aşama rayı + kapının düğmeleri).
 *
 * `product_vision_start` bilerek YOK: ürün analizi bir üretim değil, çıktısı
 * bir sonraki üretimin girdisi.
 */
export const CREATIVE_LIVE_CARD_TOOLS = {
  generate_creative: 'creative_job',
  creative_improve: 'creative_job',
  creative_resize: 'creative_job',
  creative_upscale: 'creative_job',
  creative_banner: 'creative_job',
  creative_tryon: 'creative_job',
  creative_bulk_generate: 'creative_job',
  creative_regenerate: 'creative_job',
  studio_model_create: 'creative_job',
  production_start: 'production',
  production_approve: 'production',
  production_retry_shot: 'production',
  production_set_strategy: 'production',
  production_confirm_revision: 'production',
  production_refresh: 'production',
} as const satisfies Readonly<Record<string, 'creative_job' | 'production'>>;
export type CreativeLiveCardTool = keyof typeof CREATIVE_LIVE_CARD_TOOLS;

export function creativeLiveCardKindOf(tool: string): 'creative_job' | 'production' | null {
  return Object.prototype.hasOwnProperty.call(CREATIVE_LIVE_CARD_TOOLS, tool)
    ? CREATIVE_LIVE_CARD_TOOLS[tool as CreativeLiveCardTool]
    : null;
}

/** Canlı veri şekilleri — KAYDEDİLMEZ. */
export interface CreativeCardLiveData {
  /**
   * İşlerin anlık durumu. Ekran canlı olayla (`creatives` konusu, `job:<id>`)
   * ve bağlantı yeniden kurulunca bir kez tazeler; aradaki yüzde ve kalan süre
   * `creativeJobProgress` ile YEREL saatte yürür.
   */
  creative_job: {
    /**
     * Sunucu saati (ISO) — yerel saat kayık olabilir; ekran kalan süreyi bu
     * saate göre yürütür. Eski yanıtta yok.
     */
    now?: string;
    jobs: {
      jobId: string;
      status: string;
      progress: number | null;
      etaSeconds: number | null;
      creativeId: string | null;
      errorCode?: string | null;
      /*
       * ── Dalga 5b — gerçek aşama (`CreativeJob.stage`…); eski yanıtta yok ──
       */
      stage?: CreativeJobStage | null;
      stageStartedAt?: string | null;
      /** BU AŞAMANIN beklenen süresi (`CreativeJob.etaSeconds`) — `etaSeconds` ise işin kalanı. */
      stageEtaSeconds?: number | null;
      /** Model ADI ekranda gösterilmez — yalnız `fallback`/`reason`/`step` etiketi. */
      stageDetail?: CreativeJobStageDetail | null;
      creativeType?: string | null;
      /** Sunucunun `now` anındaki yüzdesi (`creativeJobProgress`). */
      percent?: number | null;
      /** Aşama beklenen süresini aştı — `etaSeconds` o zaman `null`. */
      overdue?: boolean;
      /** İade edilen kredi (iade satırı yazıldıysa ayrılan tutar). */
      refundedCredits?: number | null;
      /**
       * TESLİM EDİLMİŞ işte kısmi iade — teslim edilemeyen ücretli parçanın
       * payı (hızlı klipte müzik; `creativeDeliveryRefundCredits`). Entegrasyon.
       */
      deliveryRefundCredits?: number | null;
      /**
       * Teslim uyarıları (`PRODUCTION_DELIVERABLE_WARNINGS`, ör.
       * `music_unavailable`) — kart "müziksiz teslim edildi" der. Entegrasyon.
       */
      warnings?: string[];
      /** Bitmiş kreatifin gerçek boyutu. */
      width?: number | null;
      height?: number | null;
      /**
       * Yalnız `studio_model` satırında — mankenin kısa ömürlü önizlemesi
       * (kreatif değil; `GET /creatives?ids=` onu tanımıyor). KAYDEDİLMEZ.
       */
      previewUrl?: string | null;
    }[];
  };
  /** Satır başına günlük CTR/sıklık eğrisi (`metrics_daily`, reklam düzeyi). */
  creative_performance: {
    series: Record<string, { date: string; ctr: number | null; frequency: number | null }[]>;
  };
  /**
   * Prodüksiyonun ANLIK hâli + kısa ömürlü adresler. Kart motor çalışırken
   * yoklar (kapıda ve terminalde durur). Kayıt yoksa (silindi) `found: false`.
   * Görünüm kartın kapısıyla canlı kapıyı karşılaştırır: yeni bir kapı açıldıysa
   * düğmeleri o kapının değil — "sıradaki adımı göster" der (eylemler sunucuda doğar).
   */
  production: {
    found: boolean;
    status: ProductionStatus | null;
    stage: ProductionStage | null;
    gate: ProductionGate | null;
    version: number | null;
    progress: { stage: ProductionStage; done: number; total: number } | null;
    credits: { reserved: number; charged: number } | null;
    errorCode: string | null;
    /**
     * SAĞLAYICI BEKLEMESİ (3 Eki 2026) — yalnız beklerken dolu. Kart hata tonu
     * olmadan "Beklemede — sağlayıcı kapasitesi" der; eski API yanıtında yok.
     */
    providerWait?: { since: string } | null;
    /** Kullanılabilir kredi — rıza düğmesinin yanında "bakiye yetmiyor" uyarısı için. */
    available: number | null;
    /**
     * Düşmemiş çekimler (≤24, sırayla) — anlık durum + kabul edilen anahtar
     * kare. Kart çekim listesi YAZILMADAN doğduysa (ilerleme görünümü) kareler
     * geldikçe buradan çizilir.
     */
    shots: { id: string; order: number; status: ProductionShotStatus; keyframeUrl: string | null }[];
    /** `<soru>.<şık>` → şık önizleme karesi. */
    previews: Record<string, string>;
    /** Take kimliği → video + poster. */
    takes: Record<string, { url: string | null; posterUrl: string | null }>;
    /** Teslim kreatif kimliği → video + kapak. */
    deliverables: Record<string, { url: string | null; posterUrl: string | null }>;
  };
}
